import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import ProgressMeter from "../components/ProgressMeter";
import ResmoMascot from "../components/ResmoMascot";
import { api, formatApiErrorDetail } from "../lib/api";
import { buildTailoredResumePdf } from "../lib/resumePdf";
import {
  ArrowLeft,
  ExternalLink,
  Loader,
  CheckCircle2,
  XCircle,
  FileText,
  Mail,
  Rocket,
  Download,
} from "lucide-react";

const STAGE_LABEL = {
  pending: "Queued",
  analyzing: "Chewing on the Job Description",
  saving: "Plating the result",
  complete: "Done",
  error: "Failed",
};

// Open URLs in new tabs with a small stagger so popup blockers don't trip,
// and download PDFs sequentially to avoid the browser collapsing multiple
// downloads into one.
async function applyAllSequentially(items, onProgress) {
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    try {
      if (it.apply_url) {
        window.open(it.apply_url, "_blank", "noopener,noreferrer");
      }
      const { data } = await api.get(`/analyses/${it.analysis_id}`);
      buildTailoredResumePdf(data);
      onProgress?.(i + 1, items.length, null);
    } catch (e) {
      onProgress?.(i + 1, items.length, e);
    }
    // small spacing between actions so the browser handles each download
    await new Promise((r) => setTimeout(r, 450));
  }
}

export default function BulkResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bulk, setBulk] = useState(null);
  const [error, setError] = useState("");
  const [applyStatus, setApplyStatus] = useState(null); // {current,total,error}
  const [applying, setApplying] = useState(false);
  const ackedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/bulk/${id}`);
        if (cancelled) return;
        setBulk(data);
        // Auto-acknowledge once the batch reaches "complete" status.
        if (data.status === "complete" && !ackedRef.current) {
          ackedRef.current = true;
          api.post(`/bulk/${id}/acknowledge`).catch(() => {});
        }
        return data;
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    };
    const interval = setInterval(async () => {
      const data = await fetchOnce();
      if (data && data.status === "complete") clearInterval(interval);
    }, 2500);
    fetchOnce();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-center" data-testid="bulk-error">
          <ResmoMascot state="hungry" size={180} />
          <div className="font-display text-3xl mt-4 uppercase">Couldn't load that batch</div>
          <p className="font-mono-rm text-sm mt-2">{error}</p>
          <button
            onClick={() => navigate("/feed-job")}
            className="rm-btn rm-btn-primary mt-6"
            data-testid="bulk-back-to-feed-btn"
          >
            Back to Feed Resmo
          </button>
        </div>
      </div>
    );
  }

  if (!bulk) {
    return (
      <div className="min-h-screen rm-paper">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-center" data-testid="bulk-loading">
          <ResmoMascot state="digesting" size={180} flavor="resume" />
          <div className="font-display text-3xl mt-4 uppercase">Loading the batch...</div>
        </div>
      </div>
    );
  }

  const allDone = bulk.status === "complete";
  const items = bulk.items || [];
  const readyItems = items.filter((it) => it.status === "complete" && it.analysis_id);
  const readyWithUrls = readyItems.filter((it) => it.apply_url);

  const runApplyAll = async () => {
    if (!readyItems.length || applying) return;
    setApplying(true);
    setApplyStatus({ current: 0, total: readyItems.length, error: null });
    await applyAllSequentially(readyItems, (current, total, err) => {
      setApplyStatus({ current, total, error: err ? String(err.message || err) : null });
    });
    setApplying(false);
  };

  return (
    <div className="min-h-screen rm-paper pb-20">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <button
          onClick={() => navigate("/feed-job")}
          className="rm-tag mb-3"
          data-testid="bulk-back-tag"
        >
          <ArrowLeft size={14} /> New batch
        </button>

        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="rm-tag mb-2" style={{ background: "#FFC145", borderColor: "#0F1B2D" }}>
              Bulk batch · {bulk.completed_count + bulk.failed_count}/{bulk.total}
            </div>
            <h1 className="font-display text-3xl md:text-5xl uppercase leading-none">
              {allDone ? (bulk.failed_count === 0 ? "All ready." : "Mostly ready.") : "Cooking up your batch."}
            </h1>
            <p className="text-sm mt-2" style={{ color: "#475569" }}>
              {allDone
                ? bulk.failed_count === 0
                  ? "Every Job Description was tailored successfully. Click any card to open its full dashboard."
                  : `${bulk.completed_count} resumes ready, ${bulk.failed_count} failed. Failed credits have been refunded.`
                : "Each Job Description runs in parallel — usually 30 to 90 seconds. You can leave this page; we'll email you when it's done."}
            </p>
          </div>
          <ResmoMascot state={allDone ? "satisfied" : "digesting"} size={140} flavor="resume" />
        </div>

        {/* Apply-to-all banner — only when at least one job is complete */}
        {readyItems.length > 0 && (
          <div
            className="rm-card-static p-4 mb-6 flex flex-wrap items-center gap-3"
            style={{ background: "#FFF7ED", border: "3px solid #FF6A00" }}
            data-testid="bulk-apply-all-banner"
          >
            <Rocket size={22} style={{ color: "#FF6A00" }} strokeWidth={2.5} />
            <div className="text-sm flex-1 min-w-[200px]">
              <div className="font-bold uppercase tracking-wide">One-click apply</div>
              <div className="font-mono-rm text-xs" style={{ color: "#475569" }}>
                {readyWithUrls.length} of {readyItems.length} ready
                {readyWithUrls.length ? ` open in new tabs` : ""} · downloads all tailored PDFs.
                {readyWithUrls.length < readyItems.length && (
                  <span> Cards without an apply URL still get their PDF.</span>
                )}
              </div>
              {applyStatus && (
                <div
                  className="font-mono-rm text-xs mt-1"
                  style={{ color: applyStatus.error ? "#991B1B" : "#0F1B2D" }}
                  data-testid="bulk-apply-all-progress"
                >
                  {applying
                    ? `Applying… ${applyStatus.current}/${applyStatus.total}`
                    : applyStatus.error
                      ? `Finished with errors: ${applyStatus.error}`
                      : `Done · ${applyStatus.current}/${applyStatus.total} applied`}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={runApplyAll}
              disabled={applying || !readyItems.length}
              className="rm-btn rm-btn-primary text-sm"
              data-testid="bulk-apply-all-btn"
              title={
                readyWithUrls.length
                  ? "Open every apply URL in a new tab and download each tailored PDF"
                  : "Download every tailored PDF (no apply URLs were saved)"
              }
            >
              {applying ? (
                <Loader size={14} className="animate-spin" />
              ) : (
                <Rocket size={14} />
              )}
              {applying ? "Applying…" : `Apply to all (${readyItems.length})`}
            </button>
          </div>
        )}

        {/* Email reminder card while running */}
        {!allDone && (
          <div
            className="rm-card-static p-4 mb-6 flex items-center gap-3"
            style={{ background: "#FFFBEB", border: "3px dashed #FF6A00" }}
            data-testid="bulk-email-notice"
          >
            <Mail size={20} style={{ color: "#FF6A00" }} strokeWidth={2.5} />
            <div className="text-sm flex-1">
              You'll get an email summary as soon as the whole batch finishes — plus an in-app
              notification on the bell icon.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="bulk-grid">
          {items.map((it, i) => (
            <BulkCard key={it.id || i} index={i} item={it} />
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link to="/history" className="rm-btn rm-btn-secondary text-xs" data-testid="bulk-open-history-btn">
            Open History
          </Link>
          <Link to="/feed-job" className="rm-btn rm-btn-ghost text-xs" data-testid="bulk-new-batch-btn">
            Tailor another batch
          </Link>
        </div>
      </div>
    </div>
  );
}

function BulkCard({ index, item }) {
  const status = item.status || "pending";
  const isDone = status === "complete";
  const isError = status === "error";
  const stage = item.stage;
  const accent = isDone ? "#10B981" : isError ? "#DC2626" : "#FF6A00";
  const [busy, setBusy] = useState(false);
  const [downloadErr, setDownloadErr] = useState("");

  const handleApply = async () => {
    if (busy) return;
    setBusy(true);
    setDownloadErr("");
    try {
      if (item.apply_url) {
        window.open(item.apply_url, "_blank", "noopener,noreferrer");
      }
      const { data } = await api.get(`/analyses/${item.analysis_id}`);
      buildTailoredResumePdf(data);
    } catch (e) {
      setDownloadErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rm-card-static p-5"
      style={{ borderColor: accent, background: isDone ? "#ECFDF5" : isError ? "#FEF2F2" : "white" }}
      data-testid={`bulk-card-${index}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="rm-label" style={{ color: accent }}>
          Job Description #{index + 1}
        </div>
        {isDone ? (
          <CheckCircle2 size={20} style={{ color: accent }} strokeWidth={2.5} />
        ) : isError ? (
          <XCircle size={20} style={{ color: accent }} strokeWidth={2.5} />
        ) : (
          <Loader size={18} className="animate-spin" style={{ color: accent }} />
        )}
      </div>

      <div className="font-bold text-base leading-tight mb-1">
        {item.job_title || "Untitled role"}
      </div>
      {item.company_name && (
        <div
          className="font-mono-rm text-xs uppercase tracking-widest mb-3"
          style={{ color: "#475569" }}
        >
          @ {item.company_name}
        </div>
      )}

      {!isDone && !isError && (
        <div className="mt-2">
          <div
            className="font-mono-rm text-xs mb-2 flex items-center gap-1.5"
            style={{ color: "#475569" }}
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: accent }}
            />
            {STAGE_LABEL[stage] || STAGE_LABEL[status] || "Waiting..."}
          </div>
          <ProgressMeter expectedSeconds={60} testid={`bulk-card-progress-${index}`} />
        </div>
      )}

      {isError && (
        <div className="text-sm" style={{ color: "#991B1B" }} data-testid={`bulk-card-${index}-error`}>
          {item.error || "Something went wrong."}
        </div>
      )}

      {isDone && item.analysis_id && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid={`bulk-card-${index}-actions`}>
          <button
            type="button"
            onClick={handleApply}
            disabled={busy}
            className="rm-btn rm-btn-primary text-xs px-3 py-1.5"
            data-testid={`bulk-card-${index}-apply-btn`}
            title={
              item.apply_url
                ? "Open the apply URL in a new tab and download the tailored PDF"
                : "Download the tailored PDF"
            }
          >
            {busy ? (
              <Loader size={12} className="animate-spin" />
            ) : item.apply_url ? (
              <Rocket size={12} />
            ) : (
              <Download size={12} />
            )}
            {item.apply_url ? "Apply + PDF" : "Download PDF"}
          </button>
          <Link
            to={`/dashboard/${item.analysis_id}?tab=tailored`}
            className="rm-btn rm-btn-secondary text-xs px-3 py-1.5"
            data-testid={`bulk-card-${index}-open-tailored`}
          >
            <FileText size={12} /> Tailored
          </Link>
          <Link
            to={`/dashboard/${item.analysis_id}`}
            className="rm-btn rm-btn-ghost text-xs px-3 py-1.5"
            data-testid={`bulk-card-${index}-open-overview`}
          >
            <ExternalLink size={12} /> Open full
          </Link>
          {downloadErr && (
            <div
              className="font-mono-rm text-xs w-full mt-1"
              style={{ color: "#991B1B" }}
              data-testid={`bulk-card-${index}-download-err`}
            >
              {downloadErr}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
